// LT-413 (LT-405 §8.4): Campaign Brief editor + viewer.
//
// Replaces the legacy ai_context editor (LT-351). After the LT-407
// schema delta + LT-409 ai_context migration, the per-offer brief lives
// on Campaign as structured columns:
//
//   purpose             ("Why we're calling")
//   target_audience
//   call_objectives
//   likely_objections   (string[])
//   scripts             (string[])
//   additional_notes
//
// Tone moved up to L1 (`agent_versions.tone`) per LT-405 §9 and is NOT
// in the brief surface anymore. Free-text "success metrics" was retired
// in favour of the structured KPI shape (LT-352, kept).
//
// File name preserved (CampaignAiContext.tsx) to minimise import churn
// during the LT-405 epic; the export names changed (CampaignAiContext*
// → CampaignBrief*). LT-414 will rename the file once the legacy
// `ai_context` column is dropped.
import React, { useMemo } from 'react';

import { Card, Input, Pill, Textarea } from '../../ui';

export const CAMPAIGN_BRIEF_BUDGET = 8000;
const PER_TEXT_FIELD_MAX = 8000;
const PER_LIST_LEN_MAX = 20;

export interface CampaignBriefValue {
  purpose?: string;
  target_audience?: string;
  call_objectives?: string;
  likely_objections?: string[];
  scripts?: string[];
  additional_notes?: string;
}

/** Build a brief value directly from a Campaign response — preferring the
 *  structured columns, falling back to the legacy bridge-view ai_context
 *  shape for the read path until LT-414 drops the column. */
export function normalizeBriefFromCampaign(campaign: {
  purpose?: string | null;
  target_audience?: string | null;
  call_objectives?: string | null;
  likely_objections?: string[] | null;
  scripts?: string[] | null;
  additional_notes?: string | null;
  ai_context?: Record<string, unknown> | null;
}): CampaignBriefValue {
  const out: CampaignBriefValue = {};
  const text = (v: string | null | undefined) =>
    typeof v === 'string' && v.trim().length > 0 ? v : undefined;
  const list = (v: string[] | null | undefined) =>
    Array.isArray(v) && v.length > 0 ? v : undefined;
  out.purpose = text(campaign.purpose);
  out.target_audience = text(campaign.target_audience);
  out.call_objectives = text(campaign.call_objectives);
  out.likely_objections = list(campaign.likely_objections);
  out.scripts = list(campaign.scripts);
  out.additional_notes = text(campaign.additional_notes);

  // Bridge-view fallback: if the structured columns are empty but the
  // computed ai_context (LT-409) carries a legacy shape, surface that
  // for read so historical campaigns don't render blank during the
  // observation window.
  const raw = campaign.ai_context;
  if (raw && typeof raw === 'object') {
    const anyRaw = raw as Record<string, unknown>;
    const legacyText = (k: string) =>
      typeof anyRaw[k] === 'string' && (anyRaw[k] as string).trim().length > 0
        ? (anyRaw[k] as string)
        : undefined;
    const legacyList = (k: string) => {
      if (!Array.isArray(anyRaw[k])) return undefined;
      const items = (anyRaw[k] as unknown[])
        .filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
      return items.length > 0 ? items : undefined;
    };
    if (!out.purpose) out.purpose = legacyText('value_prop');
    if (!out.target_audience) out.target_audience = legacyText('target_persona');
    if (!out.likely_objections) {
      out.likely_objections = legacyList('common_objections') ?? legacyList('objections');
    }
    if (!out.scripts) out.scripts = legacyList('scripts');
  }
  return out;
}

/** Approximate the server-side rendered length for the budget meter. */
export function estimateBriefRenderedLength(value: CampaignBriefValue): number {
  let total = 0;
  const text = (label: string, v: string | undefined) =>
    !v ? 0 : label.length + 2 + v.trim().length;
  const list = (label: string, items: string[] | undefined) => {
    if (!items || items.length === 0) return 0;
    let n = label.length + 2;
    for (const item of items) n += `  - ${item.trim()}\n`.length;
    return n;
  };
  total += text('Campaign goals', undefined); // campaign_goals lives outside the brief surface
  total += text('Purpose', value.purpose);
  total += text('Target audience', value.target_audience);
  total += text('Call objectives', value.call_objectives);
  total += text('Additional notes', value.additional_notes);
  total += list('Likely objections', value.likely_objections);
  total += list('Scripts', value.scripts);
  return total;
}

interface CampaignBriefEditorProps {
  value: CampaignBriefValue;
  disabled?: boolean;
  onChange: (next: CampaignBriefValue) => void;
}

export const CampaignBriefEditor: React.FC<CampaignBriefEditorProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const used = useMemo(() => estimateBriefRenderedLength(value), [value]);
  const overBudget = used > CAMPAIGN_BRIEF_BUDGET;

  const setText = (key: 'purpose' | 'target_audience' | 'call_objectives' | 'additional_notes') => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = e.target.value.slice(0, PER_TEXT_FIELD_MAX);
    onChange({ ...value, [key]: v || undefined });
  };

  const setListText = (key: 'likely_objections' | 'scripts') => (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const items = e.target.value
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .slice(0, PER_LIST_LEN_MAX);
    onChange({ ...value, [key]: items.length > 0 ? items : undefined });
  };

  const listToText = (items: string[] | undefined) =>
    items && items.length > 0 ? items.join('\n') : '';

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-200">Campaign Brief</h3>
          <p className="text-xs text-slate-500">
            Per-offer detail spliced into the agent's prompt at call time.
            The agent's L1 persona (tone / persona / objection-handling
            style) lives on the agent itself; edit there to change voice.
          </p>
        </div>
        <Pill intent={overBudget ? 'danger' : 'neutral'}>
          {used} / {CAMPAIGN_BRIEF_BUDGET}
        </Pill>
      </div>

      <div className="flex flex-col gap-3">
        <label className="block">
          <span className="text-xs text-slate-400">Why we&apos;re calling (purpose)</span>
          <Textarea
            value={value.purpose ?? ''}
            onChange={setText('purpose')}
            disabled={disabled}
            rows={2}
            placeholder="What outcome this campaign is selling. One or two sentences."
            data-testid="brief-purpose"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Target audience</span>
          <Input
            value={value.target_audience ?? ''}
            onChange={setText('target_audience')}
            disabled={disabled}
            placeholder='e.g. "VP Sales at 50–200 person SaaS companies"'
            data-testid="brief-target-audience"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Call objectives</span>
          <Textarea
            value={value.call_objectives ?? ''}
            onChange={setText('call_objectives')}
            disabled={disabled}
            rows={2}
            placeholder="What the agent should aim to do on each call (qualify, book a demo, recover a payment)."
            data-testid="brief-call-objectives"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">
            Likely objections (one per line, max {PER_LIST_LEN_MAX})
          </span>
          <Textarea
            value={listToText(value.likely_objections)}
            onChange={setListText('likely_objections')}
            disabled={disabled}
            rows={4}
            placeholder={'Already have a tool\nBudget tight\nPrefer email'}
            data-testid="brief-likely-objections"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">
            Scripts (one per line, max {PER_LIST_LEN_MAX})
          </span>
          <Textarea
            value={listToText(value.scripts)}
            onChange={setListText('scripts')}
            disabled={disabled}
            rows={4}
            placeholder={'Open with the ROI question\nIf they bring up budget, mention the trial'}
            data-testid="brief-scripts"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Additional notes</span>
          <Textarea
            value={value.additional_notes ?? ''}
            onChange={setText('additional_notes')}
            disabled={disabled}
            rows={2}
            placeholder="Anything else the agent should know that doesn't fit the structured fields."
            data-testid="brief-additional-notes"
          />
        </label>

        {overBudget ? (
          <div className="px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-xs">
            Combined length exceeds the {CAMPAIGN_BRIEF_BUDGET}-char prompt
            budget. The server will truncate trailing fields when rendered.
          </div>
        ) : null}
      </div>
    </Card>
  );
};

interface CampaignBriefPanelProps {
  value: CampaignBriefValue;
}

export const CampaignBriefPanel: React.FC<CampaignBriefPanelProps> = ({ value }) => {
  const empty =
    !value.purpose
    && !value.target_audience
    && !value.call_objectives
    && (!value.likely_objections || value.likely_objections.length === 0)
    && (!value.scripts || value.scripts.length === 0)
    && !value.additional_notes;

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-200">Campaign Brief</h3>
        {empty ? (
          <Pill intent="neutral">Not set</Pill>
        ) : (
          <Pill intent="info">In use</Pill>
        )}
      </div>
      {empty ? (
        <p className="text-xs text-slate-500">
          Add a purpose, target audience, scripts, or call objectives to give
          the agent campaign-level context. Edit the campaign to set these.
        </p>
      ) : (
        <dl className="text-sm text-slate-300 flex flex-col gap-3">
          {value.purpose ? (
            <div>
              <dt className="text-xs text-slate-500">Why we&apos;re calling</dt>
              <dd className="whitespace-pre-wrap">{value.purpose}</dd>
            </div>
          ) : null}
          {value.target_audience ? (
            <div>
              <dt className="text-xs text-slate-500">Target audience</dt>
              <dd>{value.target_audience}</dd>
            </div>
          ) : null}
          {value.call_objectives ? (
            <div>
              <dt className="text-xs text-slate-500">Call objectives</dt>
              <dd className="whitespace-pre-wrap">{value.call_objectives}</dd>
            </div>
          ) : null}
          {value.likely_objections && value.likely_objections.length > 0 ? (
            <div>
              <dt className="text-xs text-slate-500">Likely objections</dt>
              <dd>
                <ul className="list-disc list-inside space-y-0.5 mt-0.5">
                  {value.likely_objections.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
          {value.scripts && value.scripts.length > 0 ? (
            <div>
              <dt className="text-xs text-slate-500">Scripts</dt>
              <dd>
                <ul className="list-disc list-inside space-y-0.5 mt-0.5">
                  {value.scripts.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
          {value.additional_notes ? (
            <div>
              <dt className="text-xs text-slate-500">Additional notes</dt>
              <dd className="whitespace-pre-wrap">{value.additional_notes}</dd>
            </div>
          ) : null}
        </dl>
      )}
    </Card>
  );
};
